import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FaqList } from '@/components/generals/faq-list'
import { StatGrid } from '@/components/generals/stat-grid'
import { QuoteTabs } from '@/components/generals/quote-tabs'
import { GuideLinks } from '@/components/generals/guide-links'
import { ClosingCta } from '@/components/generals/closing-cta'
import { SwitchBand } from '@/components/generals/switch-band'
import { ReasonTriad } from '@/components/generals/reason-triad'
import { ProductHero } from '@/components/generals/product-hero'
import { GettingStarted } from '@/components/generals/getting-started'
import { KvalitetHeroVisual } from '@/components/landing/kvalitet/hero-visual'
import { ChecklistScene } from '@/components/landing/kvalitet/checklist-scene'
import { TemplateLibrary } from '@/components/landing/kvalitet/template-library'
import { PhotoProof } from '@/components/landing/kvalitet/photo-proof'
import { VOICES } from '@/constants/testimonials'

const FAQ: Faq[] = [
    {
        question: 'Oppfyller Svenn kravene til kvalitetssikring?',
        answer: [
            "Ja. Svenn er laget for kravene til internkontroll i plan- og bygningsloven og SAK10, og malene følger TEK17. Systemet viser hvem som gjorde hva og når, og samler alt i én rapport du kan vise frem.",
            "Hjelp med oppsett og støtte ved tilsyn er inkludert, så du står ikke alene når kontrollen kommer."
        ]
    },
    {
        question: 'Finnes det ferdige sjekklister?',
        answer: [
            "Ja, over 1000 bransjemaler for nybygg, rehab og våtrom. Du kan bruke dem som de er, eller tilpasse dem til måten dere jobber på."
        ]
    },
    {
        question: 'Hva skiller Svenn fra andre KS-systemer?',
        answer: [
            "Det korte svaret: brukervennlighet. Appen er strippet for unødvendige steg, så også de som ikke liker teknologi bruker den. Et KS-system som ikke blir brukt, dekker ingen krav."
        ]
    },
    {
        question: 'Fungerer appen uten dekning?',
        answer: [
            "Ja. Sjekklister, bilder og avvik lagres på mobilen og lastes opp automatisk når du er tilbake i et område med dekning."
        ]
    },
    {
        question: 'Hvordan fungerer prøveperioden?',
        answer: [
            "Du får full tilgang i 14 dager. Ingen kredittkort, ingen abonnement som fornyer seg selv, og det koster ingenting å avslutte."
        ]
    },
    {
        question: 'Hva koster Svenn etter prøveperioden?',
        answer: [
            "519 kr i måneden per bedrift med én bruker inkludert, og 309 kr i måneden per ekstra bruker. Fire brukere koster altså 1 446 kr i måneden. Ingen etableringsgebyr, ingen tilleggsmoduler, og support er alltid inkludert. Alle priser er eks. mva."
        ],
        link: { label: 'Se full prisoversikt', href: '/priser' }
    }
]

const Kvalitetssikring: FC = (): ReactNode => {
    return (
        <Layout header='dark'>
            <Seo
                title='Kvalitetssikring'
                description="KS-appen som faktisk blir brukt: over 1000 sjekklister, avvik, bildedokumentasjon og digital signering. Valgt av 3000+ håndverksbedrifter. Prøv gratis i 14 dager."
            />

            <ProductHero
                tone='night'
                layout='stacked'
                eyebrow='Kvalitetssikring · KS-løsningen til 3000+ håndverksbedrifter'
                titleA='KS-appen'
                titleB='som faktisk blir brukt.'
                text='Gjør jobben riktig første gang og slipp kostbare feil. Sjekklistene tar sekunder å fylle ut på byggeplassen, og ingen trenger opplæring.'
                visual={<KvalitetHeroVisual />}
                quote={{
                    quote: 'Selv våre «godt voksne» ansatte fyller ut sjekklister og laster opp bilder med Svenn-appen uten problemer.',
                    name: 'Georg Hågensen',
                    company: 'Hh Håndverk AS'
                }}
            />

            <ReasonTriad
                index='01'
                eyebrow='Foretrukket KS-løsning'
                title='Et KS-system er mer enn programvare.'
                intro='Det er rutiner, dokumentasjon og noen som stiller opp når tilsynet kommer. Derfor velger 3000+ håndverksbedrifter Svenn.'
                reasons={[
                    { title: 'Brukervennlighet først', text: 'Et system som ikke blir brukt, dekker ingen krav. Appen forklarer seg selv.' },
                    { title: 'Oppsett og dokumentasjon inkludert', text: 'Vi setter opp komplett dokumentasjon, så kravene er oppfylt fra første dag.' },
                    { title: 'Årlig revisjon og hjelp ved tilsyn', text: 'Vi går gjennom systemet hvert år og hjelper deg når tilsynet kommer. Over 500 bedrifter har bestått.' }
                ]}
            />

            <ChecklistScene />
            <TemplateLibrary />
            <PhotoProof />

            <StatGrid
                index='05'
                tone='signal'
                eyebrow='Prisen på feil'
                title='Hver byggfeil spiser av marginen din.'
                intro='Byggfeil koster bransjen milliarder hvert år. Dokumentert kvalitetssikring fanger dem tidlig, før de blir dyre.'
                stats={[
                    {
                        label: 'Nye boliger',
                        value: '3 av 4',
                        text: 'nye boliger har minst én byggfeil.',
                        note: 'Menon Economics for DiBK, 2023.'
                    },
                    {
                        label: 'Av omsetningen',
                        value: '5',
                        unit: '%',
                        text: 'går med til å rette feil. For et tømrerfirma med under 10 ansatte er det rundt 300 000 kr i året.',
                        note: 'SINTEF anslår 2–6 %. Omsetning fra SSB 2024.'
                    },
                    {
                        label: 'Bestått tilsyn',
                        value: '500+',
                        text: 'bedrifter har bestått tilsyn med Svenn.',
                        note: 'Svenns kunder per 2026.'
                    }
                ]}
            />

            <QuoteTabs
                index='06'
                tone='paper'
                title='Ekte resultater. Ekte håndverkere.'
                intro='Dette sier noen av de 3000+ bedriftene som bruker Svenn til kvalitetssikring.'
                items={[VOICES.georgKs, VOICES.joakimKs, VOICES.mikkelKs]}
            />

            <SwitchBand
                index='07'
                title='Ingen grunn til byttefrykt'
                text='Vi sørger for en sømløs overgang. Du får fri bruk ut oppsigelsestiden hos dagens leverandør, og vi hjelper deg gratis med oppsett og flytting av malene dine.'
                points={['Fri bruk ut oppsigelsestiden', 'Gratis oppsett', 'Vi flytter malene dine', 'Support alltid inkludert']}
                from='Dagens KS-system'
            />

            <GettingStarted
                index='08'
                tone='mist'
                intro='Fra konto til første utfylte sjekkliste i løpet av samme uke.'
                steps={[
                    { when: '2 minutter', title: 'Opprett konto', text: 'Ingen kort og ingen møter. Du er inne på to minutter.' },
                    { when: 'Samme uke', title: 'Vi setter opp KS-en med deg', text: 'Vi samler malene dine og hjelper deg gratis med dokumentasjonen.' },
                    { when: 'Dag 1 i bruk', title: 'Laget er i gang', text: 'De ansatte laster ned appen og fyller ut sin første sjekkliste.' }
                ]}
            />

            <FaqList items={FAQ} index='09' />

            <ClosingCta
                tone='night'
                eyebrow='Neste kontroll'
                lines={['Gjør det riktig.', 'Og bevis det.']}
                text='Prøv Svenn gratis i 14 dager. Krever ikke kredittkort, og er 100 % uforpliktende.'
                note='Deretter 519 kr i måneden per bedrift med én bruker inkludert, og 309 kr i måneden per ekstra bruker. Pris eks. mva.'
            />

            <GuideLinks
                index='10'
                title='Lær mer om kvalitetssikring'
                intro='Gratis guider og sjekklister fra bloggen.'
                guides={[
                    { kind: 'Guide', title: 'Kvalitetssikring for håndverkere: krav, regler og rutiner som holder ved tilsyn', href: '/blogg/kvalitetssikring-for-handverkere' },
                    { kind: 'Guide', title: 'Et KS-system for håndverkere som faktisk blir brukt', href: '/blogg/ks-system-for-handverkere' },
                    { kind: 'Guide og sjekklister', title: 'Våtromsnormen: komplett guide og gratis sjekklister', href: '/blogg/vatromsnormen-komplett-guide-og-gratis-sjekklister' }
                ]}
            />
        </Layout>
    )
}

export default Kvalitetssikring
